import React, { useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

/**
 * Reusable image zoom component with pinch-to-zoom and button controls
 */
const ImageZoom = ({ src, alt = 'Image', className = '', onError }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const minScale = 0.5;
  const maxScale = 5;
  const scaleStep = 0.5;

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + scaleStep, maxScale));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - scaleStep, minScale));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.01;
    const newScale = Math.min(Math.max(scale + delta, minScale), maxScale);
    setScale(newScale);
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isDragging && scale > 1) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="relative">
      {/* Zoom Controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-2">
        <button
          type="button"
          onClick={handleZoomIn}
          disabled={scale >= maxScale}
          className="bg-white hover:bg-gray-100 text-gray-700 rounded-full p-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleZoomOut}
          disabled={scale <= minScale}
          className="bg-white hover:bg-gray-100 text-gray-700 rounded-full p-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={scale === 1 && position.x === 0 && position.y === 0}
          className="bg-white hover:bg-gray-100 text-gray-700 rounded-full p-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Reset zoom"
        >
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>

      {/* Zoom Level Indicator */}
      {scale !== 1 && (
        <div className="absolute top-2 left-2 z-10 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm font-medium">
          {Math.round(scale * 100)}%
        </div>
      )}

      {/* Image Container */}
      <div
        className={`overflow-hidden ${className}`}
        onWheel={handleWheel}
        style={{ touchAction: scale > 1 ? 'none' : 'auto' }}
      >
        <img
          src={src}
          alt={alt}
          className={`max-w-full max-h-full object-contain transition-transform ${
            scale > 1 ? 'cursor-move' : 'cursor-zoom-in'
          }`}
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transformOrigin: 'center center',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onError={onError}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      <div className="mt-2 text-center text-xs text-gray-500">
        {scale > 1 ? 'Drag to pan • Scroll to zoom' : 'Scroll or use buttons to zoom'}
      </div>
    </div>
  );
};

export default ImageZoom;
